// ChatPage.jsx - Prompt model selection every time

"use client"

import { useState, useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { ArrowLeft, Send, Bot, User, Sparkles, Copy, Download, RefreshCw, MessageCircle } from "lucide-react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

const ChatPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const chatContainerRef = useRef(null)

  const selectedType = location.state?.type
  const userEmail = location.state?.email
  const purpose = location.state?.purpose

  const [selectedModel, setSelectedModel] = useState("")
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [modelSelected, setModelSelected] = useState(false)
  const [chatExists, setChatExists] = useState(false)
  const [copied, setCopied] = useState(false)

  const pageTitle =
    selectedType === "brand-story"
      ? "Brand Story Creator"
      : selectedType === "chat-bot"
        ? "Chatbot"
        : "Digital Product Concepting"

  useEffect(() => {
    if (!selectedType || !userEmail) {
      navigate("/")
      return
    }

    // Always require user to reselect model — but check if chat exists
    axios
      .get(`https://api.testir.xyz/server15/api/chat?email=${userEmail}&type=${selectedType}`)
      .then((res) => {
        setChatExists(true)
      })
      .catch((err) => {
        if (err.response?.status === 404) {
          setChatExists(false)
        } else {
          console.error("Failed to check chat existence:", err)
        }
      })

    inputRef.current?.focus()
  }, [selectedType, userEmail, navigate])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  const handleModelSelect = async (model) => {
    setSelectedModel(model)
    setModelSelected(true)

    if (chatExists) {
      try {
        const res = await axios.get(`https://api.testir.xyz/server15/api/chat?email=${userEmail}&type=${selectedType}`)
        setMessages(res.data.messages)
      } catch (err) {
        console.error("Failed to fetch existing chat:", err)
      }
    }
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return

    const userMessage = {
      id: Date.now(),
      role: "user",
      content: inputMessage,
      timestamp: new Date().toISOString(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInputMessage("")
    setIsLoading(true)

    try {
      if (!chatExists && messages.length === 0) {
        const res = await axios.post("https://api.testir.xyz/server15/api/chat/start", {
          userEmail,
          type: selectedType,
          model: selectedModel,
          initialUserMessage: userMessage.content,
        })
        setMessages(res.data.messages)
        setChatExists(true)
      } else {
        const res = await axios.post("https://api.testir.xyz/server15/api/chat/continue", {
          userEmail,
          type: selectedType,
          message: userMessage.content,
          model: selectedModel,
        })
        setMessages(res.data.messages)
      }
    } catch (err) {
      console.error("Error sending message:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetChat = async () => {
    if (!userEmail || !selectedType) return

    const confirmReset = window.confirm("Are you sure you want to reset this chat?")
    if (!confirmReset) return

    try {
      await axios.delete("https://api.testir.xyz/server15/api/chat/reset", {
        params: {
          email: userEmail,
          type: selectedType,
        },
      })

      setMessages([])
      setChatExists(false)
      setModelSelected(false)
      setSelectedModel("")
    } catch (err) {
      console.error("Failed to reset chat:", err)
      alert("Failed to reset chat. Please try again.")
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const downloadChat = () => {
    const chatText = messages
      .map((msg) => `${msg.role === "user" ? "You" : "AI"} (${formatTimestamp(msg.timestamp)}):\n${msg.content}\n\n`)
      .join("")

    const element = document.createElement("a")
    const file = new Blob([chatText], { type: "text/plain" })
    element.href = URL.createObjectURL(file)
    element.download = `chat-${selectedType}-${new Date().toISOString().slice(0, 10)}.txt`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <div className="min-h-screen text-black flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-300 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/")}
                className="h-10 w-10 bg-white border border-gray-300 rounded-full flex items-center justify-center text-black hover:bg-gray-50 transition-colors shadow-sm"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>

              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-black">{pageTitle}</h1>
                  {modelSelected && (
                    <div className="flex items-center space-x-1">
                      <div
                        className={`h-2 w-2 rounded-full ${selectedModel === "openai" ? "bg-emerald-500" : "bg-blue-500"}`}
                      ></div>
                      <span className="text-xs text-gray-600">
                        {selectedModel === "openai" ? "OpenAI" : "Deepseek"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {modelSelected && messages.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyToClipboard(messages.map((m) => `${m.role}: ${m.content}`).join("\n\n"))}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:text-black bg-white border border-gray-300 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors shadow-sm"
                >
                  <Copy className="h-3 w-3" />
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button
                  onClick={downloadChat}
                  className="flex items-center gap-1 text-xs text-gray-600 hover:text-black bg-white border border-gray-300 hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors shadow-sm"
                >
                  <Download className="h-3 w-3" />
                  Save
                </button>
                <button
                  onClick={handleResetChat}
                  className="flex items-center gap-1 text-xs text-red-600 hover:text-white bg-white border border-gray-300 hover:bg-red-500 rounded-lg px-3 py-2 transition-colors shadow-sm"
                >
                  <RefreshCw className="h-3 w-3" />
                  Reset
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {!modelSelected ? (
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <div className="max-w-md w-full bg-white/95 backdrop-blur-sm border border-gray-300 rounded-2xl p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-black mb-3">Choose Your AI Model</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Select the AI model you'd like to use for this conversation. Each model has unique capabilities.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => handleModelSelect("openai")}
                className="w-full px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-xl text-white font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/25 transform hover:scale-[1.02]"
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>OpenAI GPT</span>
                  <div className="h-2 w-2 bg-white rounded-full"></div>
                </div>
              </button>
              <button
                onClick={() => handleModelSelect("deepseek")}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl text-white font-semibold transition-all hover:shadow-lg hover:shadow-blue-500/25 transform hover:scale-[1.02]"
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>Deepseek AI</span>
                  <div className="h-2 w-2 bg-white rounded-full"></div>
                </div>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full" ref={chatContainerRef}>
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-8">
                <div className="w-20 h-20 bg-white rounded-full border border-gray-300 flex items-center justify-center mb-6 shadow-lg">
                  <Bot className="h-10 w-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-semibold text-black mb-3">Start Your Conversation</h3>
                <p className="text-gray-600 max-w-md leading-relaxed">
                  Ask a question or provide details about your{" "}
                  {selectedType === "brand-story" ? "brand story" : "digital product"} to get started.
                </p>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fadeIn`}
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className={`flex gap-4 max-w-3xl ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                    {/* Avatar */}
                    <div
                      className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center shadow-md ${
                        msg.role === "user"
                          ? "bg-gradient-to-br from-purple-500 to-pink-500"
                          : selectedModel === "openai"
                            ? "bg-gradient-to-br from-emerald-500 to-teal-600"
                            : "bg-gradient-to-br from-blue-500 to-indigo-600"
                      }`}
                    >
                      {msg.role === "user" ? (
                        <User className="h-5 w-5 text-white" />
                      ) : (
                        <Bot className="h-5 w-5 text-white" />
                      )}
                    </div>

                    {/* Message Content */}
                    <div
                      className={`flex-1 p-4 rounded-2xl bg-white border shadow-sm ${
                        msg.role === "user"
                          ? "border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50"
                          : selectedModel === "openai"
                            ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50"
                            : "border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50"
                      }`}
                    >
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                      </div>

                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-200">
                        {/* <span className="text-xs text-gray-500">{formatTimestamp(msg.timestamp)}</span> */}
                        {msg.role !== "user" && (
                          <button
                            onClick={() => copyToClipboard(msg.content)}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="border-t border-gray-300 bg-white/95 backdrop-blur-sm p-4">
            <div className="flex items-end space-x-4">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  className="w-full p-4 pr-12 bg-white text-black rounded-2xl border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 resize-none transition-all shadow-sm"
                  rows={1}
                  style={{ minHeight: "56px", maxHeight: "120px" }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={isLoading || !inputMessage.trim()}
                  className={`absolute right-3 bottom-3 p-2 rounded-xl transition-all ${
                    isLoading || !inputMessage.trim()
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg hover:shadow-purple-500/25 transform hover:scale-105"
                  }`}
                >
                  {isLoading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              {selectedModel === "openai" ? "Powered by OpenAI" : "Powered by Deepseek"} • Press Enter to send,
              Shift+Enter for new line
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChatPage
